import React, {useRef} from 'react'
import {useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Spacing} from 'types/Theme'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {
  InputButton,
  InputButtonStyledContainer
} from '@vio/atlas/helpers/InputButton'
import {Icon} from '@vio/atlas/Icon'
import {Label} from '@vio/atlas/Label'
import {truncateSingleLine} from '@vio/atlas/src/utils/truncate'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {TrackEventPayload} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {DateValidationReason} from '@vio/core/src/searchParams/dateValidationReason'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'

import {getDateConfirmationTooltipShown} from '../../search/cookies/utils'
import {datePickerMessages} from '../../search/DatePicker/helpers/datePickerMessages'
import {guestPickerMessages} from '../../search/GuestPicker/guestPickerMessages'
import {SearchDateTooltipWrapper} from '../../search/SearchDateTooltipWrapper'
import {
  DateTooltipAnchor,
  DateTooltipWrapper
} from '../../search/SearchDateTooltipWrapper/SearchDateTooltipWrapper'
import {
  DestinationInput,
  destinationPickerMessages,
  SearchFormDestinationInput
} from './SearchFormDestinationInput'

interface BaseProps {
  /** Check in date */
  checkIn: string
  /** Check out date */
  checkOut: string
  /** Rooms configuration */
  numberOfAdults: number
  /** Rooms configuration */
  numberOfChildren: number
  /** Rooms configuration */
  numberOfRooms: number
  /** Callback for check-in date picker click */
  onCheckInDatePickerClick: () => void
  /** Callback for check-out date picker click */
  onCheckOutDatePickerClick: () => void
  /** Callback for Guest picker click */
  onGuestPickerClick: () => void
  /** Callback for Submit click */
  onSubmit: () => void
  /** Whether the check in picker popover is currently open */
  isCheckInPickerOpen: boolean
  /** Whether the check out picker popover is currently open */
  isCheckOutPickerOpen: boolean
  /** Whether the guest picker popover is currently open */
  isGuestPickerOpen: boolean
  /** A ref to the guest picker container */
  guestPickerRef: React.RefObject<HTMLDivElement>
  /** Whether the rooms count and label is shown */
  isRoomsLabelShown?: boolean
  /** Whether the guest picker is hidden */
  hideGuestPicker?: boolean
  /** Wether should show the date confirmation tooltip */
  isDateConfirmationTooltipShown?: boolean
  /** The date validation reason for scenario-specific tooltip messages */
  dateValidationReason?: DateValidationReason | null
  /** If true, ignore cookie check and show tooltip based solely on isDateConfirmationTooltipShown (for Kiwi brand) */
  ignoreCookieCheck?: boolean
  /** Track event function. Temporary solution to track events without crashing until redux analytics middleware is added.*/
  trackEvent: (payload: TrackEventPayload) => void
  /** Whether the search form is on the home page */
  isHomePage?: boolean
}

interface PropsWithDestination extends BaseProps {
  /** Destination name displayed in the destination button */
  destinationButtonDisplayValue: string
  /** Destination name displayed in the destination input */
  destinationInputDisplayValue: string
  /** Callback for Destination picker click */
  onDestinationPickerClick: (
    event: React.SyntheticEvent<Element, Event>
  ) => void
  /** A ref to the destination input */
  destinationInputRef?: React.RefObject<HTMLDivElement>
  /** Whether the destination picker popover is currently open */
  isDestinationPickerOpen: boolean
  /** Callback for suggestions requested */
  onSuggestionsRequested: (
    value: string,
    shouldLoadUserSearchHistory: boolean
  ) => void
  /** A flag to see if the destination input has error */
  destinationHasError: boolean
}

interface PropsWithoutDestination extends BaseProps {
  destinationButtonDisplayValue: never
  destinationInputDisplayValue: never
  onDestinationPickerClick: never
  destinationInputRef: never
  isDestinationPickerOpen: never
  onSuggestionsRequested: never
  destinationHasError: never
}

export type Props = PropsWithDestination | PropsWithoutDestination

const DESTINATION_BASE_WIDTH = 190
const DATE_BASE_WIDTH = 124
const GUEST_BASE_WIDTH = 182
const KIWI_INPUT_BORDER_RADIUS = 3

export const LabelWrapper = styled(Text)`
  white-space: nowrap;
  color: ${cssTheme.colors.input.default.icon};
  margin-right: ${cssTheme.layout.spacing.s100};
`

const IconWrapper = styled.div``

const FormInputButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s200};
`

const DateWrapper = styled(FormInputButtonWrapper)`
  flex: 1 1 ${DATE_BASE_WIDTH}px !important;
`

const SearchFormGrid = styled.div<{gap: Spacing}>`
  width: 100%;
  display: flex;
  align-self: flex-start;
  min-width: 0;
  gap: ${({gap}) => cssTheme.layout.spacing[gap]};
  ${DateTooltipAnchor} {
    width: 100px;
    left: 8px;
  }
  ${DateTooltipWrapper} {
    display: flex;
    flex-wrap: nowrap;
    position: relative;
    outline: 2px solid ${cssTheme.colors.input.focus.border};
    transition: outline 0.2s ease;
    border-radius: ${cssTheme.layout.radius.xs};
    outline-offset: 1px;
    flex: 2 1 230px !important;
    gap: ${cssTheme.layout.spacing.s500};
    margin-left: calc(${cssTheme.layout.spacing.s500} / 2);
    margin-right: calc(${cssTheme.layout.spacing.s500} / 2);
    ${DateWrapper} {
      flex: 1 1 ${DATE_BASE_WIDTH}px !important;
      position: relative;

      ${IconWrapper} {
        color: ${cssTheme.colors.content.brand.primary};
      }
    }
  }
`

export const FormContainer = styled.div`
  width: 100%;
`

const KiwiFormContainer = styled.div`
  width: 100%;
  display: flex;
  ${SearchFormGrid} {
    ${InputButtonStyledContainer} {
      border-radius: ${KIWI_INPUT_BORDER_RADIUS}px;
      padding-right: ${cssTheme.layout.spacing.s400};
      gap: ${cssTheme.layout.spacing.s400};
    }
    ${DestinationInput} {
      label {
        border-radius: ${KIWI_INPUT_BORDER_RADIUS}px;
        gap: ${cssTheme.layout.spacing.s400};
      }
    }
    ${LabelWrapper} {
      margin-right: 0;
    }
`

const DestinationWrapper = styled(FormInputButtonWrapper)`
  flex: 2 1 ${DESTINATION_BASE_WIDTH}px !important;
  min-width: 0;
`
const GuestWrapper = styled(FormInputButtonWrapper)`
  flex: 1 1 ${GUEST_BASE_WIDTH}px !important;
`

const SearchButtonWrapper = styled(Grid)<{isBottomAligned?: boolean}>`
  flex: 1 1 !important;
  max-width: 200px;
  align-self: ${({isBottomAligned}) =>
    isBottomAligned ? 'flex-end' : 'flex-start'};
`

const FormButton = styled(InputButton)`
  ${InputButtonStyledContainer} {
    border-radius: ${cssTheme.layout.radius.md};
    text-align: left;
  }
  ${IconWrapper} {
    color: ${cssTheme.colors.input.default.icon};
  }

  &:hover {
    ${InputButtonStyledContainer} {
      border-color: ${cssTheme.colors.input.focus.border};
    }
  }
  &:focus {
    ${InputButtonStyledContainer} {
      border-color: ${cssTheme.colors.input.focus.border};
      box-shadow: 0 0 0 1px ${cssTheme.colors.input.focus.border};
    }
    ${IconWrapper} {
      color: ${cssTheme.colors.content.interactive.primary} !important;
    }
  }
  &.--is-active {
    background: ${cssTheme.colors.input.default.background};

    ${InputButtonStyledContainer} {
      background: initial;
      border-color: ${cssTheme.colors.input.focus.border};
    }
    ${IconWrapper} {
      color: ${cssTheme.colors.content.interactive.primary} !important;
    }
  }
  &.--has-error {
    padding: 0;

    ${InputButtonStyledContainer} {
      border-color: ${cssTheme.colors.input.error.border};
    }
    ${IconWrapper} {
      color: ${cssTheme.colors.input.default.icon};
    }
    background: ${cssTheme.colors.input.default.background};

    ${InputButtonStyledContainer} {
      background: initial;
    }
    &:hover {
      ${InputButtonStyledContainer} {
        border-color: ${cssTheme.colors.input.error.border};
      }
    }
    &:focus {
      border-color: ${cssTheme.colors.input.error.border};
    }
  }
`

const ButtonContent = styled.div`
  ${truncateSingleLine()}
`

const PlaceholderWrapper = styled.div`
  color: ${cssTheme.colors.input.default.label.empty};
`

export const SearchFormHorizontalFullWidth = React.memo(
  function SearchFormHorizontalFullWidth({
    checkIn,
    checkOut,
    destinationButtonDisplayValue,
    destinationInputDisplayValue,
    numberOfAdults,
    numberOfChildren,
    numberOfRooms,
    onDestinationPickerClick,
    onCheckInDatePickerClick,
    onCheckOutDatePickerClick,
    onGuestPickerClick,
    onSubmit,
    isDestinationPickerOpen,
    isCheckInPickerOpen,
    isCheckOutPickerOpen,
    isGuestPickerOpen,
    isHomePage,
    guestPickerRef,
    onSuggestionsRequested,
    destinationInputRef,
    destinationHasError = false,
    isRoomsLabelShown = true,
    isDateConfirmationTooltipShown = false,
    dateValidationReason,
    ignoreCookieCheck = false,
    trackEvent
  }: Props) {
    const {formatDate, formatMessage} = useIntl()
    const {shell} = useBrandCapabilities()
    const alternativeSearchFormSettings = shell.hasAlternativeSearchForm.enabled
      ? shell.hasAlternativeSearchForm.config
      : undefined
    const {isDesktopXs} = useDeviceLayout()
    const {hasOutsideInputLabels, hasTextInputPrefix, inputsGap} =
      alternativeSearchFormSettings || {}

    const showOutsideInputLabels = hasOutsideInputLabels && isHomePage
    const showDestination = Boolean(onDestinationPickerClick)
    const isTextLabel = hasTextInputPrefix && !isDesktopXs
    const numberOfGuests = numberOfAdults + numberOfChildren

    const guestMessage = formatMessage(guestPickerMessages.guest, {
      count: numberOfGuests
    })

    const roomsMessage = formatMessage(guestPickerMessages.room, {
      count: numberOfRooms
    })

    const checkInDate =
      checkIn &&
      formatDate(dateStringToMiddayDate(checkIn), {
        day: '2-digit',
        month: 'short'
      })

    const checkOutDate =
      checkOut &&
      formatDate(dateStringToMiddayDate(checkOut), {
        day: '2-digit',
        month: 'short'
      })

    // Check cookie only once when component mounts with isDateConfirmationTooltipShown = true
    // Use ref to prevent recalculation on re-renders (which would hide tooltip after cookie is set)
    // Skip cookie check for Kiwi brand (ignoreCookieCheck = true)
    const hasValidationReason = Boolean(dateValidationReason)
    const cookieCheckedRef = useRef(false)
    const initialCookieShownRef = useRef<boolean | null>(null)

    if (
      !ignoreCookieCheck &&
      !hasValidationReason &&
      !cookieCheckedRef.current &&
      isDateConfirmationTooltipShown
    ) {
      initialCookieShownRef.current = getDateConfirmationTooltipShown()
      cookieCheckedRef.current = true
    }

    // Validation-based tooltips always bypass the cookie check
    const showDateConfirmationTooltip =
      isDateConfirmationTooltipShown &&
      (ignoreCookieCheck ||
        hasValidationReason ||
        initialCookieShownRef.current === false)

    const FormContainerComponent = shell.hasFlexSearchFormContainer
      ? KiwiFormContainer
      : FormContainer

    return (
      <FormContainerComponent>
        <SearchFormGrid gap={inputsGap as Spacing}>
          {showDestination && (
            <DestinationWrapper>
              {showOutsideInputLabels && (
                <Label
                  variant="bodyS"
                  value={
                    <FormattedMessageWrapper
                      id="location"
                      defaultMessage="Location"
                    />
                  }
                  forId="DestinationButton"
                />
              )}
              <Box position="relative">
                <FormButton
                  size="md"
                  id="DestinationButton"
                  dataId="DestinationButton"
                  onClick={onDestinationPickerClick}
                  icon={
                    !isTextLabel ? (
                      <IconWrapper>
                        <Icon name="Pin" />
                      </IconWrapper>
                    ) : undefined
                  }
                  placeholder={formatMessage(
                    destinationPickerMessages.destinationPlaceholder
                  )}
                  className={cx(
                    isDestinationPickerOpen && '--is-active',
                    destinationHasError && '--has-error'
                  )}
                >
                  {(isTextLabel || destinationButtonDisplayValue) && (
                    <>
                      {isTextLabel && (
                        <LabelWrapper variant="labelS">
                          <FormattedMessageWrapper
                            id="location"
                            defaultMessage="Location"
                          />
                        </LabelWrapper>
                      )}
                      {!destinationButtonDisplayValue && (
                        <PlaceholderWrapper>
                          {formatMessage(
                            destinationPickerMessages.destinationPlaceholder
                          )}
                        </PlaceholderWrapper>
                      )}

                      {destinationButtonDisplayValue && (
                        <ButtonContent data-id="DestinationValue" dir="auto">
                          {destinationButtonDisplayValue}
                        </ButtonContent>
                      )}
                    </>
                  )}
                </FormButton>
                {isDestinationPickerOpen && (
                  <div ref={destinationInputRef}>
                    <SearchFormDestinationInput
                      hasError={destinationHasError}
                      focusOnMount
                      shouldLoadUserSearchHistory
                      destination={destinationInputDisplayValue}
                      onSuggestionsRequested={onSuggestionsRequested}
                      isDestinationPickerOpen={isDestinationPickerOpen}
                      isTextLabel={isTextLabel}
                    />
                  </div>
                )}
              </Box>
            </DestinationWrapper>
          )}
          <SearchDateTooltipWrapper
            isDateConfirmationTooltipShown={showDateConfirmationTooltip}
            dateValidationReason={dateValidationReason}
            trackEvent={trackEvent}
          >
            <DateWrapper>
              {showOutsideInputLabels && (
                <Label
                  variant="bodyS"
                  value={
                    <FormattedMessageWrapper
                      id="checkIn"
                      defaultMessage="Check-in"
                    />
                  }
                  forId="CheckInInput"
                />
              )}
              <Box position="relative">
                <FormButton
                  size="md"
                  id="CheckInInput"
                  dataId="CheckInInput"
                  onClick={onCheckInDatePickerClick}
                  icon={
                    !isTextLabel ? (
                      <IconWrapper>
                        <Icon name="CalendarStart" />
                      </IconWrapper>
                    ) : undefined
                  }
                  placeholder={formatMessage(datePickerMessages.checkInDate)}
                  className={cx(isCheckInPickerOpen && '--is-active')}
                >
                  {isTextLabel && (
                    <LabelWrapper variant="labelS">
                      <FormattedMessageWrapper
                        id="checkIn"
                        defaultMessage="Check-in"
                      />
                    </LabelWrapper>
                  )}
                  {checkInDate && <ButtonContent>{checkInDate}</ButtonContent>}
                </FormButton>
              </Box>
            </DateWrapper>
            <DateWrapper>
              {showOutsideInputLabels && (
                <Label
                  variant="bodyS"
                  value={
                    <FormattedMessageWrapper
                      id="checkOut"
                      defaultMessage="Check-out"
                    />
                  }
                  forId="CheckOutInput"
                />
              )}
              <Box position="relative">
                <FormButton
                  size="md"
                  id="CheckOutInput"
                  dataId="CheckOutInput"
                  onClick={onCheckOutDatePickerClick}
                  icon={
                    !isTextLabel ? (
                      <IconWrapper>
                        <Icon name="CalendarEnd" />
                      </IconWrapper>
                    ) : undefined
                  }
                  placeholder={formatMessage(datePickerMessages.checkOutDate)}
                  className={cx(isCheckOutPickerOpen && '--is-active')}
                >
                  {isTextLabel && (
                    <LabelWrapper variant="labelS">
                      <FormattedMessageWrapper
                        id="checkOut"
                        defaultMessage="Check-out"
                      />
                    </LabelWrapper>
                  )}
                  <ButtonContent>{checkOutDate && checkOutDate}</ButtonContent>
                </FormButton>
              </Box>
            </DateWrapper>
          </SearchDateTooltipWrapper>

          <GuestWrapper ref={guestPickerRef}>
            {showOutsideInputLabels && (
              <Label
                variant="bodyS"
                value={
                  <FormattedMessageWrapper
                    id="guests"
                    defaultMessage="Guests"
                  />
                }
                forId="GuestInput"
              />
            )}
            <Box position="relative">
              <FormButton
                size="md"
                id="GuestInput"
                dataId="GuestInput"
                onClick={onGuestPickerClick}
                icon={
                  !isTextLabel ? (
                    <IconWrapper>
                      <Icon name="People" />
                    </IconWrapper>
                  ) : undefined
                }
                className={cx(isGuestPickerOpen && '--is-active')}
              >
                {isTextLabel && (
                  <LabelWrapper variant="labelS">
                    <FormattedMessageWrapper
                      id="guests"
                      defaultMessage="Guests"
                    />
                  </LabelWrapper>
                )}
                <ButtonContent>
                  {guestMessage}
                  {isRoomsLabelShown && <>, {roomsMessage}</>}
                </ButtonContent>
              </FormButton>
            </Box>
          </GuestWrapper>
          <SearchButtonWrapper isBottomAligned={showOutsideInputLabels}>
            <Button
              size="lg"
              fullWidth
              dataId="SearchButton"
              onClick={onSubmit}
              iconStart={<Icon name="Search" />}
            >
              <FormattedMessageWrapper id="search" defaultMessage="Search" />
            </Button>
          </SearchButtonWrapper>
        </SearchFormGrid>
      </FormContainerComponent>
    )
  }
)
